import type { ApiRentalDealItem } from 'src/types/rental';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios from 'src/utils/axios';
import { requestSession } from 'src/utils/storefrontSession';

import { QueryKeys } from './QueryKeys';

/**
 * Report the transaction the renter's own wallet created the escrow contract with, from the terms
 * sheet `prepare-payment` answered (api#380). The api verifies it on chain, records the address and
 * moves the booking to `deploy_confirmed`; 409 `ESCROW_TX_PENDING` until the receipt is deep
 * enough (the dialog retries), 400 for a transaction that is not this booking's creation.
 * Idempotent. The deal it answers with is cached under the session that sent it (app#248).
 */
export function useRentalEscrowCreated() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    onMutate: () => ({ session: requestSession() }),
    mutationFn: async ({ id, txHash }: { id: string; txHash: string }) => {
      const response = await axios.post<ApiRentalDealItem>(`/buyer/rental-deals/${id}/escrow-created`, { txHash });

      return response.data;
    },
    onSuccess: (data, _variables, sent) => {
      queryClient.setQueryData(
        QueryKeys.forSession(QueryKeys.rentalDeal(data.id), sent.session),
        data
      );
      queryClient.invalidateQueries({ queryKey: QueryKeys.rentalDeals() });
    },
  });

  return {
    ...mutation,
    reportEscrowCreated: (id: string, txHash: string) => mutation.mutateAsync({ id, txHash }),
  };
}
